import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as patientActions from "../../core/actions/patientActions";
import { patientServices } from "../../core/services";
import personDetailsService from "../../core/services/personDetailsService";
import Breadcrumbs from "../erp-billing/Breadcrumbs";
import SignaturePad from "./SignaturePad";
import { doctorServices } from "../../core/services";
import { ConsentFormStatus } from "../../shared/constants";
import { helperServices } from "../../shared/helper";

/**
 * ConsentFormBase
 *
 * Shared page used by both SurgeryConsentForm.js and AdmissionConsentForm.js.
 * Standalone route with independent data fetch (getPatientProfile +
 * getVisitListById + getPatientProcedureInfo), rendered inside the normal
 * app layout (sidebar/navbar visible), same as other pages in the HMS.
 *
 * Saves to /v1/procedure (via patientActions.addToProcedure), ONE ROW PER FIELD,
 * all rows sharing the same `groupname` so they can be re-fetched together via
 * GET /v1/procedure/group?groupname=... (same pattern dischargeSummary.js uses).
 *
 * props:
 *   - match: react-router match object (patientid, visitid params)
 *   - history: react-router history object
 *   - groupname: string, e.g. "Surgery Consent" | "Admission Consent"
 *   - templateType: exact form_type stored in form_text_content
 *   - formTitle: string, page heading
 *   - Consent text is loaded dynamically from the database using groupname.
 *   - English and Kannada content are stored in form_text_content.
 *   - Supported placeholders:
 *   - {{patientName}}
 *   - {{diagnosis}}
 *   - {{doctorName}}
 *  
 *   - includeWardAndDiagnosis: bool - Admission Consent historically did not show
 *     IP No / Ward / Diagnosis in the old PDF (see investigation report). Defaults to true.
 *   - PrintComponent: the existing print component to reuse for the Print button
 *     (Components.PrintSurgeryConsent or Components.PrintAdmissionConsent)
 *
 * Witness Signature (optional, supports MULTIPLE witnesses):
 *   - "+ Add Witness Signature" always stays visible and can be clicked
 *     repeatedly - each click appends a new witness block below the last one
 *     (Witness Name / Relationship / Signature Pad), each with its own
 *     "Remove" button. Clicking Add smooth-scrolls to the newly added block.
 *   - All witnesses for this form are saved as ONE row, claSS = "WitnessList",
 *     whose notes is a JSON string: [{ name, relation, signature, signedAt }, ...]
 *     (signature = base64 PNG from SignaturePad.toDataURL). This mirrors how
 *     ConsentTemplateMetadata already stores a JSON blob in a single field,
 *     and avoids needing a variable number of claSS keys for a variable
 *     number of witnesses.
 */

const parseTemplateContent = (content) => {
  if (!content) return [];

  try {
    const parsed = JSON.parse(content);
    return Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch (error) {
    return [String(content)];
  }
};

const ConsentFormBase = (props) => {
  const dispatch = useDispatch();
  const patientId = props.match.params.patientid;
  const visitId = props.match.params.visitid;
  const subgroup = props.match.params.subgroup;
  const includeWardAndDiagnosis = props.includeWardAndDiagnosis !== false;

  const isNewMode = new URLSearchParams(
    props.location?.search || ""
  ).get("mode") === "new";
  
  console.log("Patient ID : , visitId : , subgroup : , isNewMode :   ", patientId, visitId, subgroup, isNewMode);
  
  const [header, setHeader] = useState({
    patientName: "",
    age: "",
    gender: "",
    uhid: "",
    ipNo: "",
    ward: "",
    date: helperServices.formatDateOnly(new Date()),
    TreatingDoctor: "",
    Diagnosis: "",
  });

  const [language, setLanguage] = useState("en"); // "en" | "kn"
  const [showFieldsCard, setShowFieldsCard] = useState(true);

  const [templates, setTemplates] = useState({
    en: [],
    kn: [],
  });

  const [templateMetadata, setTemplateMetadata] = useState({
    EN: {
      templateId: null,
      version: null,
    },
    KN: {
      templateId: null,
      version: null,
    },
  });

  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateError, setTemplateError] = useState("");

  const [canPatientSign, setCanPatientSign] = useState(true);
  const [guardian, setGuardian] = useState({ name: "", relation: "" });

  const [patientSigUrl, setPatientSigUrl] = useState(null);
  const [guardianSigUrl, setGuardianSigUrl] = useState(null);
  const [doctorSigUrl, setDoctorSigUrl] = useState(null); // pre-filled from org config, if available

  const [patientSignedAt, setPatientSignedAt] = useState(null);
  const [guardianSignedAt, setGuardianSignedAt] = useState(null);
  const [doctorSignedAt, setDoctorSignedAt] = useState(null);

  const [doctorGvId, setDoctorGvId] = useState(null);
  const [doctorList, setDoctorList] = useState([]);

  // ---- Witness Signature state (supports multiple witnesses) ----
  // Each entry: { id, name, relation, sigUrl } - sigUrl is the *saved*
  // base64 signature (from DB, or after a fresh capture is retained across
  // re-renders); the live unsaved pad strokes live inside SignaturePad
  // itself and are read out via witnessSigRefs on save.
  const [witnesses, setWitnesses] = useState([]);
  const witnessSigRefs = useRef({});   // id -> SignaturePad ref instance
  const witnessNodeRefs = useRef({});  // id -> DOM node, for scroll-to-new
  const lastAddedWitnessIdRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(""); // "", "saved", "error"
  const [saveError, setSaveError] = useState("");
  const [showPrint, setShowPrint] = useState(false);
  const [alreadySaved, setAlreadySaved] = useState(false);
  const [formStatus, setFormStatus] = useState(ConsentFormStatus.inprogress);
  const existingRowIdsRef = useRef({}); // claSS -> row id, for update-in-place

  const patientSigRef = useRef(null);
  const guardianSigRef = useRef(null);
  const doctorSigRef = useRef(null);



  useEffect(() => {
    // Sidebar/navbar intentionally left untouched — this page now uses the
    // same layout as the rest of the app (per your request), unlike
    // Discharge Summary which strips the sidebar out.

    // Load consent text from database
    patientServices
      .getFormTextContent(props.templateType || props.groupname)
      .then((res) => {
        console.log("Form text content API response:", res);

        if (!Array.isArray(res)) {
          setTemplateError("Invalid form text response");
          return;
        }

        console.log("Form text content rows:", res);

        const englishRow = res.find(
          (item) => item.language === "EN"
        );

        const kannadaRow = res.find(
          (item) => item.language === "KN"
        );

        console.log("english row, kan row : ", englishRow, kannadaRow);

        setTemplates({
          en: parseTemplateContent(englishRow?.content),
          kn: parseTemplateContent(kannadaRow?.content),
        });

        setTemplateMetadata({
          EN: {
            templateId: englishRow?.id || null,
            version: englishRow?.version || null,
          },
          KN: {
            templateId: kannadaRow?.id || null,
            version: kannadaRow?.version || null,
          },
        });
        setTemplateLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load form text content:", err);
        setTemplateError("Failed to load consent form text");
        setTemplateLoading(false);
      });

    // Header field auto-fill
    dispatch(
      patientActions.getPatientProfile(patientId, visitId, (resCb) => {
        if (!resCb) return;
        setHeader((prev) => ({
          ...prev,
          patientName: `${resCb.fname || ""} ${resCb.mname || ""} ${resCb.lname || ""}`
            .replace(/\s+/g, " ")
            .trim(),
          age: resCb.age || "",
          gender: resCb.gender || "",
          uhid: resCb.id || "",
          ipNo: resCb.visit?.ipopnumber || resCb.ipopnumber || "",
          ward: resCb.room?.roomName || "",
        }));
      })
    );

    // Treating doctor comes from the CURRENT DOCTOR assignment.
    //
    // In this application:
    // - groupname == null       -> Doctor assignment
    // - groupname == "new session" -> Therapy / session booking
    //
    // Therefore, "new session" must NOT be treated as the doctor
    // for the Consent Form.
    //
    // Priority:
    // 1. Latest OPEN doctor assignment (groupname == null)
    // 2. Resolve doctor from personList
    // 3. Resolve doctor from personDetailsService cache
    // 4. Fallback to firstVisit.person

    dispatch(
      patientActions.getVisitListById(
        visitId,
        "showAllVisits",
        (resCb) => {
          const visitListData = resCb?.patsearchreslt || [];
          const firstVisit = visitListData[0];

          if (!firstVisit) return;

          // ---------------------------------------------------------
          // 1. Get all assignments for this visit
          // ---------------------------------------------------------
          const currentVisitAssignments =
            firstVisit?.visitAssignmentList || [];

          // ---------------------------------------------------------
          // 2. Build person lookup from API response
          // ---------------------------------------------------------
          const personLookup = {};

          if (Array.isArray(resCb?.personList)) {
            resCb.personList.forEach((person) => {
              if (person?.personId != null) {
                personLookup[person.personId] = person;
              }
            });
          }

          // ---------------------------------------------------------
          // 3. Find the latest OPEN DOCTOR assignment
          //
          // Important:
          // groupname == null means doctor assignment
          // "new session" is NOT considered here because it
          // represents a session/therapy booking.
          // ---------------------------------------------------------
          const latestDoctorAssignment = [
            ...currentVisitAssignments,
          ]
            .reverse()
            .find(
              (assignment) =>
                assignment.groupname == null &&
                assignment.targetid &&
                assignment.status === "open"
            );

          let doctor = null;

          // ---------------------------------------------------------
          // 4. Resolve doctor using targetid -> personList
          // ---------------------------------------------------------
          if (latestDoctorAssignment) {
            doctor =
              personLookup[latestDoctorAssignment.targetid];
          }

          // ---------------------------------------------------------
          // 5. Fallback to doctorsDetail cache
          // ---------------------------------------------------------
          if (
            !doctor &&
            latestDoctorAssignment?.targetid
          ) {
            const doctorsDetail =
              personDetailsService.getDoctorsDetail();

            if (Array.isArray(doctorsDetail)) {
              const foundDoctor = doctorsDetail.find(
                (doc) =>
                  doc &&
                  (
                    doc.personId ==
                    latestDoctorAssignment.targetid ||
                    doc.gvId ==
                    latestDoctorAssignment.targetid ||
                    doc.gvid ==
                    latestDoctorAssignment.targetid
                  )
              );

              if (foundDoctor) {
                doctor = {
                  personId:
                    foundDoctor.personId ||
                    foundDoctor.gvId ||
                    foundDoctor.gvid,

                  gvId:
                    foundDoctor.gvId ||
                    foundDoctor.gvid ||
                    foundDoctor.personId,

                  first_name:
                    foundDoctor.first_name ||
                    foundDoctor.firstName ||
                    "",

                  last_name:
                    foundDoctor.last_name ||
                    foundDoctor.lastName ||
                    "",

                  displaytitle:
                    foundDoctor.displaytitle ||
                    foundDoctor.title ||
                    "",
                };
              }
            }
          }

          // ---------------------------------------------------------
          // 6. Final fallback to firstVisit.person
          // ---------------------------------------------------------
          if (!doctor && firstVisit.person) {
            doctor = firstVisit.person;
          }

          // ---------------------------------------------------------
          // 7. Set doctor name and doctor ID
          // ---------------------------------------------------------
          if (doctor) {
            const doctorName =
              `${doctor.displaytitle || ""} ${doctor.first_name || ""
                } ${doctor.last_name || ""}`.trim();

            setHeader((prev) => ({
              ...prev,
              TreatingDoctor:
                doctorName || prev.TreatingDoctor,
            }));

            const gvId =
              doctor.gvId ||
              doctor.gvid ||
              doctor.personId;

            if (gvId != null) {
              setDoctorGvId(gvId);
            }
          }
        }
      )
    );


    //remove the diagnosis and ward details from other procedures
    // dispatch(
    //   patientActions.getPatientProcedureInfo(patientId, visitId, "showAllVisits", (resp) => {
    //     const list = resp?.patientresp?.procedureslist || resp?.procedureslist;
    //     if (Array.isArray(list)) {
    //       const complaint = list.find(
    //         (proc) => proc.claSS === "complaint" && String(proc.visitId) === String(visitId)
    //       );
    //       if (complaint?.complaint) {
    //         setHeader((prev) => ({ ...prev, diagnosis: complaint.complaint }));
    //       }
    //     }
    //   })
    // );

    doctorServices.getDoctorList().then((res) => {
      console.log("RAW doctorList response :", res);
      setDoctorList(res?.personList || []);
    }).catch((err) => {
      console.error("getDoctorList Failed:", err);
    });

    console.log("getDischargeSummary api call start : ", isNewMode);
    // Pre-fill if this form was already saved before (reopening for review)
    if (!isNewMode) {
      console.log("Reopening form for review from getDischargeSummary : ", isNewMode);
      patientServices
        .getDischargeSummary({}, { patientId, visitId, groupname: props.groupname, subgroup })
        .then((resCb) => {
          console.log("getDischargeSummary api resp : ", resCb);          
          const list = resCb?.procedureList;
          console.log("proc api resp list : ", list)
          if (!Array.isArray(list) || list.length === 0) return;
          setAlreadySaved(true);
          const byClass = {};
          list.forEach((row) => {
            byClass[row.claSS] = row.notes;
            if (row.id != null) {
              existingRowIdsRef.current[row.claSS] = row.id;
            }
          });
          setFormStatus(byClass.status || ConsentFormStatus.inprogress);
          console.log("log byclass : , existingRowIdsRef : ", byClass, existingRowIdsRef);
          if (byClass.ConsentTemplateMetadata) {
            try {
              const metadata = JSON.parse(byClass.ConsentTemplateMetadata);
              if (metadata?.templateMetadata) {
                setTemplateMetadata({
                  EN: {
                    templateId:
                      metadata.templateMetadata.EN?.templateId || null,
                    version:
                      metadata.templateMetadata.EN?.version || null,
                  },
                  KN: {
                    templateId:
                      metadata.templateMetadata.KN?.templateId || null,
                    version:
                      metadata.templateMetadata.KN?.version || null,
                  },
                });
                if (metadata.templateMetadata.selectedLanguage) {
                  setLanguage(
                    metadata.templateMetadata.selectedLanguage.toLowerCase()
                  );
                }
              }
            } catch (e) {
              console.error(
                "Failed to parse saved consent template metadata:",
                e
              );
            }
          }
          console.log("header : , byclass : ", header, byClass);
          setHeader((prev) => ({
            ...prev,
            patientName: byClass.PatientName || prev.patientName,
            age: byClass.Age || prev.age,
            gender: byClass.Gender || prev.gender,
            uhid: byClass.UHID || prev.uhid,
            ipNo: byClass.IPNo || prev.ipNo,
            ward: byClass.Ward || prev.ward,
            date: byClass.Date || prev.date,
            TreatingDoctor: byClass.TreatingDoctor || prev.TreatingDoctor,
            Diagnosis: byClass.Diagnosis || prev.Diagnosis,
          }));
          console.log("Header: ",header);
          
          if (byClass.GuardianName) {
            setCanPatientSign(false);
            setGuardian({
              name: byClass.GuardianName || "",
              relation: byClass.GuardianRelation || "",
            });
          }
          // Resolve saved signature images. Base64 only - old upload-ID format
          // is no longer supported for reads.
          if (byClass.PatientSignature) {
            setPatientSigUrl(byClass.PatientSignature);
          }
          if (byClass.GuardianSignature) {
            setGuardianSigUrl(byClass.GuardianSignature);
          }
          if (byClass.PatientSignedAt) {
            setPatientSignedAt(byClass.PatientSignedAt);
          }

          if (byClass.GuardianSignedAt) {
            setGuardianSignedAt(byClass.GuardianSignedAt);
          }

          if (byClass.DoctorSignedAt) {
            setDoctorSignedAt(byClass.DoctorSignedAt);
          }

          if (byClass.DoctorSignature) {
            setDoctorSigUrl(byClass.DoctorSignature);
          }
          // Witnesses: stored as one JSON row (claSS = "WitnessList").
          // Rehydrate into the witnesses[] state, each getting a stable id
          // so SignaturePad/remove logic can target the right entry.
          if (byClass.WitnessList) {
            try {
              const parsedWitnesses = JSON.parse(byClass.WitnessList);

              if (Array.isArray(parsedWitnesses) && parsedWitnesses.length > 0) {
                const loadedWitnesses = parsedWitnesses.map((w, idx) => ({
                  id: `witness_saved_${idx}_${Date.now()}`,
                  name: w.name || "",
                  relation: w.relation || "",
                  sigUrl: w.signature || null,
                  signedAt: w.signedAt || null,
                }));

                setWitnesses(loadedWitnesses);
              }
            } catch (e) {
              console.error("Failed to parse saved witness list:", e);
            }
          }
        })
        .catch((error) => {
          /* no prior save yet - fine, this is a fresh form */
          console.log("getDischargeSummary api call failed : ", error);
          
        });
    } else {
      console.log("new document data");
      
      // Fresh form: make sure nothing from a previous save leaks in
      setAlreadySaved(false);
      setFormStatus(ConsentFormStatus.inprogress);
      existingRowIdsRef.current = {};
      setTemplateMetadata({
        EN: { templateId: null, version: null },
        KN: { templateId: null, version: null },
      });
      setCanPatientSign(true);
      setGuardian({ name: "", relation: "" });
      setPatientSigUrl(null);
      setGuardianSigUrl(null);
      setWitnesses([]);
      witnessSigRefs.current = {};
      witnessNodeRefs.current = {};
    }
    console.log("getDischargeSummary api call end ");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate effect for the doctor signature lookup, deliberately NOT part of
  // the mount effect above. On a normal in-app navigation, org data is
  // already loaded and this fires once with everything ready. On a hard
  // page refresh, org?.orgCode can still be undefined when the mount effect
  // runs (App.js's org fetch is async) - this effect re-runs automatically
  // once org finishes loading (or once gvId arrives, whichever is later),
  // so the lookup always gets a real chance to succeed either way.
  /*useEffect(() => {
    if (doctorGvId != null) {
      const preStoredSignature = org?.doctorSignatures?.[doctorGvId];
      if (preStoredSignature) {
        setDoctorSigUrl(preStoredSignature);
      }
    }
  }, [org, doctorGvId]);*/


  useEffect(() => {
    console.log("doctorList :", doctorList);
    console.log("looking for doctorGvId", doctorGvId);
    if (doctorGvId != null && doctorList.length > 0) {
      const matchedDoctor = doctorList.find((p) => p && String(p.gvId) === String(doctorGvId));
      console.log("matchedDoctor :", matchedDoctor);
      if (matchedDoctor?.signature) {
        setDoctorSigUrl(matchedDoctor.signature);
      }
    }
  }, [doctorList, doctorGvId]);

  // ---- Witness Signature handlers ----
  // Add button stays visible always (doesn't disappear) so multiple
  // witnesses can be added one after another. Each click appends a fresh
  // block below the existing ones and smooth-scrolls to it once it's
  // actually rendered.
  const handleAddWitnessClick = () => {
    const newId = `witness_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    lastAddedWitnessIdRef.current = newId;
    setWitnesses((prev) => [
      ...prev,
      { id: newId, name: "", relation: "", sigUrl: null },
    ]);
  };

  useEffect(() => {
    if (lastAddedWitnessIdRef.current) {
      const targetId = lastAddedWitnessIdRef.current;
      lastAddedWitnessIdRef.current = null;
      // Defer to next paint so the new block is actually in the DOM/layout
      // before we ask the browser to scroll to it.
      const raf = requestAnimationFrame(() => {
        witnessNodeRefs.current[targetId]?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [witnesses]);

  const updateWitnessField = (id, field, value) => {
    setWitnesses((prev) =>
      prev.map((w) => (w.id === id ? { ...w, [field]: value } : w))
    );
  };

  const handleRemoveWitness = (id) => {
    setWitnesses((prev) => prev.filter((w) => w.id !== id));
    delete witnessSigRefs.current[id];
    delete witnessNodeRefs.current[id];
  };

  const fillTemplate = (paragraphs) =>
    (Array.isArray(paragraphs) ? paragraphs : [paragraphs]).map((paragraph) =>
      String(paragraph)
        .replace(
          /{{patientName}}/g,
          header?.patientName || "________________"
        )
        .replace(
          /{{diagnosis}}/g,
          header?.Diagnosis || "________________________________"
        )
        .replace(
          /{{doctorName}}/g,
          header?.TreatingDoctor || "________________"
        )
    );

  const saveRow = async (claSS, notes) => {
    const existingId = existingRowIdsRef.current[claSS];
    const response = await patientServices.addToProcedure({
      ...(existingId != null ? { id: existingId } : {}),
      claSS,
      notes: notes != null ? String(notes) : "",
      patientId,
      visitId,
      groupname: props.groupname,
      subgroup,
    });

    if (response?.errorMessages?.length) {
      throw new Error(response.errorMessages[0]);
    }

    const returnedProcedure =
      response?.procedure || response?.procedureList?.[0];
    if (returnedProcedure?.id != null) {
      existingRowIdsRef.current[claSS] = returnedProcedure.id;
    }

    return response;
  };

  const handleSave = async () => {
    if (formStatus === ConsentFormStatus.completed) {
      alert("This consent form is completed and cannot be edited.");
      return false;
    }

    const patientNeedsNewSig = canPatientSign && !patientSigUrl;
    const guardianNeedsNewSig = !canPatientSign && !guardianSigUrl;

    const now = new Date().toISOString();

    if (patientNeedsNewSig && patientSigRef.current?.isEmpty()) {
      alert("Please capture the patient's signature before saving.");
      return false;
    }
    if (guardianNeedsNewSig && guardianSigRef.current?.isEmpty()) {
      alert("Please capture the guardian's/attender's signature before saving.");
      return false;
    }
    if (!canPatientSign && !guardian.name.trim()) {
      alert("Please enter the guardian's/attender's name.");
      return false;
    }

    // Witnesses are optional overall, but any block that's on screen must be
    // filled in properly - a half-filled witness block is worse than none.
    // Validate + assemble the JSON payload up front, before flipping into
    // the saving state, same as the patient/guardian checks above.
    const witnessRowsData = [];

    for (let i = 0; i < witnesses.length; i++) {
      const w = witnesses[i];
      const padRef = witnessSigRefs.current[w.id];

      const hasExistingSig = !!w.sigUrl;
      const hasNewSig =
        !hasExistingSig && padRef && !padRef.isEmpty();

      if (!w.name.trim()) {
        alert(
          `Please enter a name for Witness ${i + 1}, or remove that section.`
        );
        return false;
      }

      if (!hasNewSig && !hasExistingSig) {
        alert(
          `Please capture the signature for Witness ${i + 1} (${w.name}), or remove that section.`
        );
        return false;
      }

      const signature = hasExistingSig
        ? w.sigUrl
        : padRef.toDataURL();

      const signedAt = w.signedAt || now;

      witnessRowsData.push({
        name: w.name,
        relation: w.relation,
        signature,
        signedAt,
      });

      // IMPORTANT:
      // Update React state so Print immediately after Save
      // has the signature and date/time.
      setWitnesses((prev) =>
        prev.map((item) =>
          item.id === w.id
            ? {
              ...item,
              sigUrl: signature,
              signedAt,
            }
            : item
        )
      );
    }

    setSaving(true);
    setSaveStatus("");
    setSaveError("");

    try {
      const rows = [
        ["PatientName", header.patientName],
        ["Age", header.age],
        ["Gender", header.gender],
        ["UHID", header.uhid],
        ["Date", header.date],
        ["TreatingDoctor", header.TreatingDoctor],
        ["status", ConsentFormStatus.inprogress],
        [
          "ConsentTemplateMetadata",
          JSON.stringify({
            templateMetadata: {
              EN: {
                templateId: templateMetadata.EN.templateId,
                version: templateMetadata.EN.version,
              },
              KN: {
                templateId: templateMetadata.KN.templateId,
                version: templateMetadata.KN.version,
              },
              selectedLanguage: language.toUpperCase(),
            },
          }),
        ],

      ];
      if (includeWardAndDiagnosis) {
        rows.push(["IPNo", header.ipNo], ["Ward", header.ward], ["Diagnosis", header.Diagnosis]);
      }

      if (canPatientSign) {
        if (patientSigUrl) {
          rows.push(
            ["PatientSignature", patientSigUrl],
            ["PatientSignedAt", patientSignedAt || ""]
          );
        } else if (patientSigRef.current && !patientSigRef.current.isEmpty()) {
          const dataUrl = patientSigRef.current.toDataURL();

          setPatientSigUrl(dataUrl);
          setPatientSignedAt(now);

          rows.push(
            ["PatientSignature", dataUrl],
            ["PatientSignedAt", now]
          );
        }
      }

      if (!canPatientSign) {
        rows.push(
          ["GuardianName", guardian.name],
          ["GuardianRelation", guardian.relation]
        );

        if (guardianSigUrl) {
          rows.push(
            ["GuardianSignature", guardianSigUrl],
            ["GuardianSignedAt", guardianSignedAt || ""]
          );
        } else if (guardianSigRef.current && !guardianSigRef.current.isEmpty()) {
          const dataUrl = guardianSigRef.current.toDataURL();

          setGuardianSigUrl(dataUrl);
          setGuardianSignedAt(now);

          rows.push(
            ["GuardianSignature", dataUrl],
            ["GuardianSignedAt", now]
          );
        }
      }

      if (doctorSigUrl) {
        const signedAt = doctorSignedAt || now;
        setDoctorSigUrl(doctorSigUrl);
        setDoctorSignedAt(signedAt);
        
        rows.push(
          ["DoctorSignedAt", signedAt],
          ["DoctorSignature", doctorSigUrl]
        );
      } else {
        const doctorSignUrl = doctorSigRef.current?.toDataURL(); // ensure doctor sig is captured too, if present
        const signedAt = doctorSignedAt || now;
        setDoctorSigUrl(doctorSignUrl);
        setDoctorSignedAt(signedAt);
        rows.push(
          ["DoctorSignedAt", signedAt],
          ["DoctorSignature", doctorSignUrl],
        );

      }

      // Witnesses: one JSON row for the whole list. If the user removed all
      // witnesses that were previously saved, write an empty string to clear
      // the row rather than leaving stale data behind.
      if (witnessRowsData.length > 0) {
        rows.push([
          "WitnessList",
          JSON.stringify(
            witnessRowsData
          ),
        ]);
      } else if (existingRowIdsRef.current["WitnessList"]) {
        rows.push(["WitnessList", ""]);
      }

      // Sequential to stay consistent with how ProcedureBP.insertNotes handles
      // one Procedure object per call - avoids parallel-write surprises.
      for (const [claSS, notes] of rows) {
        // eslint-disable-next-line no-await-in-loop
        await saveRow(claSS, notes);
      }

      setSaveStatus("saved");
      setAlreadySaved(true);
      return true;
    } catch (e) {
      console.error("Consent form save failed:", e);
      setSaveError(e?.message || String(e) || "Unknown error");
      setSaveStatus("error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    if (saving) return;

    if (!alreadySaved) {
      const saved = await handleSave();
      if (!saved) return;
    }

    setShowPrint(true);
  };

  const crumbs = [
    {
      label: `${(header.patientName || "").toUpperCase()} ${header.age} ${header.gender}`.trim(),
      path: `/PatientSections/${patientId}/${visitId}`,
    },
    {
      label: "Consent Forms",
      path: `/PatientSections/${patientId}/${visitId}/consent-forms`,
    },
    { label: props.formTitle },
  ];

  const cardStyle = {
    background: "#fff",
    border: "1px solid #e3e6ea",
    borderRadius: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    padding: 20,
    height: "100%",
  };

  // Applied to the right-hand card so it can scroll internally instead of
  // clipping the witness signature pads on short viewports (mobile especially).
  const scrollableCardStyle = {
    ...cardStyle,
    maxHeight: "calc(100vh - 140px)",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  };
  const isCompleted = formStatus === ConsentFormStatus.completed;

  return (
    <div className="mainsection">
      <Breadcrumbs crumbs={crumbs} />

      {isCompleted && (
        <div className="alert alert-info py-2" role="status">
          This consent form is completed and is available in read-only mode.
        </div>
      )}

      <fieldset
        disabled={isCompleted}
        style={{
          border: 0,
          margin: 0,
          minWidth: 0,
          padding: 0,
          pointerEvents: isCompleted ? "none" : "auto",
        }}
      >
      <div className="row mt-2">
        {/* LEFT CARD - fields + Doctor Signature - collapsible */}
        {showFieldsCard && (
          <div className="col-md-5 mb-3">
            <div style={{ ...cardStyle, position: "relative" }}>
              <button
                onClick={() => setShowFieldsCard(false)}
                style={{
                  position: "absolute",
                  top: 2,
                  right: 8,
                  padding: "4px 12px",
                  fontSize: 40,
                  cursor: "pointer",
                  color: "#1a73e8",
                  lineHeight: 1,
                }}
                title="Collapse"
              >
                «
              </button>
              <div className="row">
                {includeWardAndDiagnosis && (
                  <div className="col-md-6 mb-2">
                    <label style={{ fontSize: 12, color: "#666" }}>Ward</label>
                    <input
                      className="form-control"
                      value={header.ward}
                      onChange={(e) => setHeader((prev) => ({ ...prev, ward: e.target.value }))}
                    />
                  </div>
                )}
                <div className="col-md-6 mb-2">
                  <label style={{ fontSize: 12, color: "#666" }}>Date</label>
                  <input
                    className="form-control"
                    value={header.date}
                    onChange={(e) => setHeader((prev) => ({ ...prev, date: e.target.value }))}
                  />
                </div>
                <div className="col-md-6 mb-2">
                  <label style={{ fontSize: 12, color: "#666" }}>Treating Doctor</label>
                  <input
                    className="form-control"
                    value={header.TreatingDoctor}
                    readOnly
                  />
                </div>
                {includeWardAndDiagnosis && (
                  <div className="col-md-6 mb-2">
                    <label style={{ fontSize: 12, color: "#666" }}>Diagnosis</label>
                    <input
                      className="form-control"
                      value={header.Diagnosis}
                      onChange={(e) =>
                        setHeader((prev) => ({ ...prev, Diagnosis: e.target.value }))
                      }
                    />
                  </div>
                )}
              </div>

              <hr />

              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Doctor Signature
              </div>
              <SignaturePad
                ref={doctorSigRef}
                label="Doctor Signature"
                existingImageUrl={doctorSigUrl}
                onRetake={() => setDoctorSigUrl(null)}
              />
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                Date &amp; time recorded automatically on save.
              </div>
            </div>
          </div>
        )}

        {/* RIGHT CARD - language toggle + consent text + Patient/Guardian Signature + Witness(es) */}
        <div className={showFieldsCard ? "col-md-7 mb-3" : "col-md-12 mb-3"}>
          <div style={{ ...scrollableCardStyle, position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
              {!showFieldsCard && (
                <button
                  type="button"
                  onClick={() => setShowFieldsCard(true)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 40,
                    lineHeight: 1,
                    height: 26,
                    cursor: "pointer",
                    color: "#1a73e8",
                    marginRight: 10,
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    flexShrink: 0,
                  }}
                  title="Expand fields"
                >
                  »
                </button>
              )}
              <div
                role="group"
                aria-label="Select language"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 26,
                  border: "1px solid #1a73e8",
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  onClick={() => setLanguage("en")}
                  style={{
                    padding: "0 12px",
                    height: "100%",
                    fontSize: 12,
                    border: "none",
                    cursor: "pointer",
                    background: language === "en" ? "#1a73e8" : "#fff",
                    color: language === "en" ? "#fff" : "#1a73e8",
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage("kn")}
                  style={{
                    padding: "0 12px",
                    height: "100%",
                    fontSize: 12,
                    border: "none",
                    borderLeft: "1px solid #1a73e8",
                    cursor: "pointer",
                    background: language === "kn" ? "#1a73e8" : "#fff",
                    color: language === "kn" ? "#fff" : "#1a73e8",
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  ಕನ್ನಡ
                </button>
              </div>
            </div>

            <div lang={language}>
              {templateLoading ? (
                <p>Loading consent form text...</p>
              ) : templateError ? (
                <p className="text-danger">{templateError}</p>
              ) : (
                fillTemplate(
                  language === "en" ? templates.en : templates.kn
                ).map((paragraph, index) => (
                  <p key={index} style={{ textAlign: "justify" }}>
                    {paragraph}
                  </p>
                ))
              )}
            </div>

            <hr />

            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
              Patient / Guardian Signature
            </div>
            <div style={{ marginBottom: 8 }}>
              <label>
                <input
                  type="checkbox"
                  checked={canPatientSign}
                  onChange={(e) => setCanPatientSign(e.target.checked)}
                />{" "}
                Patient is able to sign
              </label>
            </div>
            {canPatientSign ? (
              <SignaturePad
                ref={patientSigRef}
                label="Patient Signature"
                existingImageUrl={patientSigUrl}
                onRetake={() => setPatientSigUrl(null)}
              />
            ) : (
              <>
                <div className="mb-2">
                  <label style={{ fontSize: 12, color: "#666" }}>Guardian / Attender Name</label>
                  <input
                    className="form-control"
                    value={guardian.name}
                    onChange={(e) => setGuardian((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label style={{ fontSize: 12, color: "#666" }}>Relation with Patient</label>
                  <input
                    className="form-control"
                    value={guardian.relation}
                    onChange={(e) =>
                      setGuardian((prev) => ({ ...prev, relation: e.target.value }))
                    }
                  />
                </div>
                <SignaturePad
                  ref={guardianSigRef}
                  label="Guardian / Attender Signature"
                  existingImageUrl={guardianSigUrl}
                  onRetake={() => setGuardianSigUrl(null)}
                />
              </>
            )}
            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
              Date &amp; time recorded automatically on save.
            </div>

            {/* ---- Witness Signature(s) ---- */}
            {/* Button ALWAYS visible (never hidden after click) so multiple
                witnesses can be added, one block per click. */}
            {witnesses.map((w, idx) => (
              <div
                key={w.id}
                ref={(el) => {
                  witnessNodeRefs.current[w.id] = el;
                }}
                style={{
                  marginTop: 18,
                  paddingTop: 14,
                  borderTop: "1px dashed #ccc",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    Witness {idx + 1}
                  </div>
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger"
                    style={{ padding: 0 }}
                    onClick={() => handleRemoveWitness(w.id)}
                  >
                    Remove
                  </button>
                </div>

                <div className="mb-2">
                  <label style={{ fontSize: 12, color: "#666" }}>Witness Name</label>
                  <input
                    className="form-control"
                    value={w.name}
                    onChange={(e) => updateWitnessField(w.id, "name", e.target.value)}
                  />
                </div>
                <div className="mb-2">
                  <label style={{ fontSize: 12, color: "#666" }}>
                    Relationship to Patient
                  </label>
                  <input
                    className="form-control"
                    value={w.relation}
                    onChange={(e) => updateWitnessField(w.id, "relation", e.target.value)}
                  />
                </div>

                <SignaturePad
                  ref={(el) => {
                    witnessSigRefs.current[w.id] = el;
                  }}
                  label={`Witness ${idx + 1} Signature`}
                  existingImageUrl={w.sigUrl}
                  onRetake={() => updateWitnessField(w.id, "sigUrl", null)}
                />
                <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                  Date &amp; time recorded automatically on save.
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              style={{ marginTop: 14 }}
              onClick={handleAddWitnessClick}
            >
              + Add Witness Signature
            </button>
          </div>
        </div>
      </div>

      </fieldset>

      {/* FLOATING ACTION BUTTONS */}
      <div
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          display: "flex",
          alignItems: "center",
          gap: 12,
          background: "#ffffff",
          padding: "10px 18px",
          borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          zIndex: 200,
          border: "1px solid #e2e8f0",
        }}
      >
        {saveStatus === "saved" && (
          <span className="text-success fw-bold" style={{ fontSize: 13 }}>
            ✓ Saved
          </span>
        )}
        {saveStatus === "error" && (
          <span
            className="text-danger fw-bold"
            style={{ fontSize: 13 }}
            title={saveError}
          >
            ✕ Save failed{saveError ? `: ${saveError}` : ""}
          </span>
        )}
        <button className="btn btn-outline-primary btn-sm" onClick={handlePrint} disabled={saving}>
          Print
        </button>
        {!isCompleted && (
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : alreadySaved ? "Update" : "Save"}
          </button>
        )}
      </div>

      {showPrint && props.PrintComponent && (
        <props.PrintComponent
          patientInfo={{
            // Existing patient information
            fullName: header.patientName,
            age: header.age,
            gender: header.gender,
            id: header.uhid,
            ipopnumber: header.ipNo,
            roomName: header.ward,
            doctorName: header.TreatingDoctor,
            diagnosis: header.Diagnosis,
            patientId,
            visitId,

            // Doctor
            doctorSignature: doctorSigUrl,
            doctorSignedDate: helperServices.formatDateOnly(doctorSignedAt, true),
            doctorSignedTime: helperServices.formatTimeOnly(doctorSignedAt),

            // Patient
            patientSignature: patientSigUrl,
            patientSignedDate: helperServices.formatDateOnly(patientSignedAt, true),
            patientSignedTime: helperServices.formatTimeOnly(patientSignedAt),

            // Guardian
            guardianName: guardian.name,
            guardianRelation: guardian.relation,
            guardianSignature: guardianSigUrl,
            guardianSignedDate: helperServices.formatDateOnly(guardianSignedAt, true),
            guardianSignedTime: helperServices.formatTimeOnly(guardianSignedAt),

            // Witnesses
            witnesses: witnesses.map((w) => ({
              name: w.name || "",
              relation: w.relation || "",
              signature: w.sigUrl || null,
              signedAt: w.signedAt || null,
            })),
          }}
          regDate={header.date}
          parentCallBack={() => setShowPrint(false)}
        />
      )}
    </div>
  );
};

export default ConsentFormBase;
